import { Icon } from "@multiagentbook/ui";
import X from "lucide-react/icons/x";
import MenuIcon from "lucide-react/icons/menu";
import Moon from "lucide-react/icons/moon";
import Sun from "lucide-react/icons/sun";
import Github from "lucide-react/icons/github";
import { useState } from "react";
import { appContext } from "../../hooks/provider";
import { Link } from "react-router-dom";
import React from "react";
import { truncateText } from "@multiagentbook/ui";
import { auth } from "../auth/firebase/firebase";
import { LanguageSwitcher } from "../common";
import { useTranslation } from "react-i18next";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

interface HeaderProps {
  user: any;
  links: any[];
  link: string;
  meta?: any;
}

const Header: React.FC<HeaderProps> = ({ user, links, link }) => {
  const { t } = useTranslation("common");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
      }
      setIsUserMenuOpen(false);
      window.location.href = "/";
    } catch (error) {
      // Error handling could be added here if needed
    }
  };

  const DarkModeToggle = () => {
    return (
      <appContext.Consumer>
        {(context) => {
          return (
            <button
              onClick={() => {
                context.setDarkMode(
                  context.darkMode === "light" ? "dark" : "light"
                );
              }}
              className="flex-shrink-0 bg-primary p-1 text-secondary rounded-full hover:text-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
            >
              <span className="sr-only">{t("header.toggleDarkMode")}</span>
              {context.darkMode === "dark" && (
                <Moon className="h-6 w-6" aria-hidden="true" />
              )}
              {context.darkMode === "light" && (
                <Sun className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          );
        }}
      </appContext.Consumer>
    );
  };

  return (
    <nav className="bg-primary mt-4 text-primary mb-8 border-b border-secondary">
      <div className="px-0 sm:px-0 lg:px-0">
        <div className="flex justify-between h-16">
          <div className="flex lg:px-0">
            <div className="flex flex-shrink-0 pt-2">
              <a className="block hover:cursor-pointer" href="/#">
                <span className="text-accent inline-block pt-2 absolute">
                  <Icon icon="app" size={8} />
                </span>
                <div className="pt-1 text-lg ml-14 inline-block">
                  <div className="flex flex-col">
                    <div className="text-base">{t("header.designing")}</div>
                    <div className="text-xs">{t("header.multiAgentSystems")}</div>
                  </div>
                </div>
              </a>
            </div>

            <div className="hidden md:ml-6 md:flex md:space-x-6">
              {links.map((data, index) => {
                const isActive =
                  data.href === link ||
                  (link.startsWith(data.href) && data.href !== "/");
                const activeClass = isActive ? "bg-accent" : "bg-secondary";

                return (
                  <div
                    key={index + "linkrow"}
                    className="text-primary items-center hover:text-accent   px-1 pt-1 block text-sm font-medium"
                  >
                    <Link
                      className="hover:text-accent h-full flex flex-col"
                      to={data.href}
                    >
                      <div className="flex-1 flex-col flex">
                        <div className="flex-1"></div>
                        <div className="pb-2 px-3">
                          {data.name || data.title}
                        </div>
                      </div>
                      <div
                        className={`${activeClass} w-full h-1 rounded-t-lg`}
                      ></div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex space-x-2 items-center">
                <LanguageSwitcher />
                <a
                  href="https://github.com/victordibia/designing-multiagent-systems"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 bg-primary p-1 text-secondary rounded-full hover:text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                  aria-label="View GitHub repository"
                >
                  <Github className="h-6 w-6" aria-hidden="true" />
                </a>
                <DarkModeToggle />
              </div>
            </div>
            <div className="hidden md:ml-1 md:flex-shrink-0 md:flex md:items-center">
              {user ? (
                <div className="ml-1 relative flex-shrink-0">
                  <div>
                    <button
                      className="bg-primary rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    >
                      <span className="sr-only">{t("header.openUserMenu")}</span>
                      <img
                        className="h-8 w-8 rounded-full border-2 border-accent"
                        src={
                          user.photoURL ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            user.displayName || "User"
                          )}&background=3b82f6&color=fff&size=128`
                        }
                        alt={user.displayName || "User"}
                        onError={(e) => {
                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            user.displayName || "U"
                          )}&background=3b82f6&color=fff&size=128`;
                        }}
                      />
                    </button>
                  </div>

                  {isUserMenuOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-primary ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <button
                        onClick={handleSignOut}
                        className="block px-4 py-2 text-sm text-primary hover:bg-secondary w-full text-left"
                      >
                        {t("nav.signOut")} {truncateText(user.displayName || user.email, 15)}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="ml-3 text-primary hover:text-accent text-sm font-medium transition-colors"
                >
                  {t("nav.signIn")}
                </Link>
              )}
            </div>
            <div className="-mr-2 flex md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-secondary hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent"
              >
                <span className="sr-only">{t("header.openMainMenu")}</span>
                {isMobileMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {links.map((data, index) => {
              const isActive = data.href === link;
              return (
                <Link
                  key={index}
                  to={data.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={classNames(
                    isActive
                      ? "bg-accent text-white"
                      : "text-primary hover:bg-secondary hover:text-accent",
                    "block px-3 py-2 rounded-md text-base font-medium"
                  )}
                >
                  {data.name || data.title}
                </Link>
              );
            })}
          </div>
          {user ? (
            <div className="pt-4 pb-3 border-t border-secondary">
              <button
                onClick={handleSignOut}
                className="block px-3 py-2 rounded-md text-base font-medium text-primary hover:text-accent hover:bg-secondary w-full text-left"
              >
                {t("nav.signOut")} {truncateText(user.displayName || user.email, 15)}
              </button>
            </div>
          ) : (
            <div className="pt-4 pb-3 border-t border-secondary">
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium text-primary hover:text-accent hover:bg-secondary"
              >
                {t("nav.signIn")}
              </Link>
            </div>
          )}
          <div className="pt-4 pb-3 border-t border-secondary flex items-center justify-center">
            <a
              href="https://github.com/victordibia/designing-multiagent-systems"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-secondary hover:text-primary px-3 py-2"
              aria-label="View GitHub repository"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Github className="h-6 w-6 mr-2" />
              <span className="text-base font-medium">{t("nav.viewOnGitHub")}</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
